<template>
    <div class="create-chatbot-wrapper">
        <WebsiteChatbotBuilder v-if="websiteBuilder" @cancel="() => (websiteBuilder = false)" />
        <div class="create-chatbot" v-else>
            <div class="sticky-header">
                <div class="title">{{$t("chatbotv2.setUpYourChatbot")}}</div>
                <div class="description">
                    {{$t("chatbotv2.setUpYourChatbotDesc")}}
                </div>
                <SupsisButton class="btnCloseCb" small @click="close">
                    <i class="material-icons">close</i>
                </SupsisButton>
            </div>

            <div class="items">
                <div class="block block-ai block-ai-bot" @click="openAIBotWizard">
                    <div class="ai-badge">
                        <span class="default-tag">{{$t("chatbotv2.recommended")}}</span>
                        <span>{{ $t("ArtificialIntelligence") }}</span>
                    </div>
                    <div class="icon">
                        <i class="material-icons">psychology</i>
                    </div>
                    <div class="name">{{ $t("chatbotv2.aiBot") }}</div>
                    <div class="description">{{$t("chatbotv2.aiBotDesc")}}</div>
                </div>
                <div class="block block-scratch" @click="createBlankStory">
                    <div class="icon">
                        <i class="material-icons">add</i>
                    </div>
                    <div class="name">{{$t("chatbotv2.blankBot")}}</div>
                    <div class="description">{{$t("chatbotv2.blankBotDesc")}}</div>
                </div>
                <div class="block block-ai" @click="channelNotAvailable">
                    <div class="ai-badge">
                        <span class="lang">{{ $t("chatbotv2.englishTurkish") }}</span>
                        <span>{{ $t("ArtificialIntelligence") }}</span>
                    </div>
                    <div class="icon">
                        <i class="material-icons">public</i>
                    </div>
                    <div class="name">{{ $t("chatbotv2.website") }}</div>
                    <div class="description">
                        {{$t("chatbotv2.websiteDesc")}}
                    </div>
                </div>
                <div class="divider-items">
                    <div class="half-template-area">
                        <a-divider class="template-divider">{{ $t("chatbotv2.readyTemplates") }}</a-divider>
                        <div class="half-template-items first">
                            <div
                                class="block block-ai block-template"
                                v-for="template in getFilteredTemplates()"
                                @click="selectedTemplate = template"
                                :key="template.slug"
                            >
                                <div class="ai-badge">
                                    <span>{{ $t("chatbotv2.basicTemplate") }}</span>
                                </div>
                                <div class="icon">
                                    <img :src="template.imageUrl" alt="" />
                                </div>
                                <div class="name">{{ template.name[currentLang] }}</div>
                                <div class="description">{{ template.shortDescription[currentLang] }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="half-template-area">
                        <a-divider class="template-divider">{{ $t("chatbotv2.templatesFromTheIndustry") }}</a-divider>

                        <div class="half-template-items">
                            <div
                                class="block block-ai block-template"
                                v-for="template in getFilteredAiTemplates()"
                                @click="selectedTemplate = template"
                                :key="template.slug"
                            >
                                <div class="ai-badge">
                                    <span>{{ $t("chatbotv2.proTemplate") }}</span>
                                </div>
                                <div class="icon">
                                    <img :src="template.imageUrl" alt="" />
                                </div>
                                <div class="name">{{ template.name[currentLang] }}</div>
                                <div class="description">{{ template.shortDescription[currentLang] }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Modal2
            id="openAI"
            v-if="openAIModal"
            :showTitle="false"
            :showFooter="false"
            @cancel="openAIModal = false"
            class="open-ai-wrapper"
        >
            <div class="open-ai-modal-body">
                <ApiKeyIcon style="width: 400px; height: 250px" />
                <p class="open-ai-modal-text">
                    {{$t("chatbotv2.yourNeedEnterYourOpenAi")}}
                </p>
                <div class="delete-modal-btn">
                    <button class="push-btn-give-up" @click="openAIModal = false">{{$t("message.cancel")}}</button>
                    <button class="push-btn" @click="openAIChannelCreate">{{$t("chatbotv2.createOpenAiKey")}}</button>
                </div>
            </div>
        </Modal2>
        <a-modal
            centered
            :visible="!!selectedTemplate"
            :footer="null"
            :closable="true"
            @cancel="selectedTemplate = null"
            v-if="selectedTemplate"
            class="template-modal"
        >
            <div class="modal-import-template">
                <div class="description">
                    <div class="top">
                        <img :src="selectedTemplate.imageUrl" :alt="selectedTemplate.name" class="image" />
                        <div class="header">
                            <div class="title">
                                {{ selectedTemplate.name[currentLang] }}
                            </div>
                            <a-button
                                type="primary"
                                ghost
                                :disbled="importing"
                                :loading="importing"
                                @click="() => createStory(selectedTemplate.slug)"
                                :disabled="!selectedTemplate.enabled"
                            >
                                {{ importing ? $t("chatbotv2.importing") : $t("chatbotv2.importBot") }}
                            </a-button>
                        </div>
                    </div>
                    <div class="tpl-markdown description-content">
                        <p>
                            {{ selectedTemplate.description[currentLang].split("\n")[0] }}
                        </p>
                        <div
                            v-if="
                                selectedTemplate.description[currentLang].split('\n')[3] !== '' &&
                                !isThatAI(selectedTemplate)
                            "
                        >
                            <p>
                                <strong>{{ $t("chatbotv2.keyFeatures") }}:</strong>
                            </p>
                            <ul>
                                <li
                                    v-for="(keyFeature, index) of $_.drop(
                                        selectedTemplate.description[currentLang].split('\n'),
                                        3
                                    )"
                                    :key="index"
                                >
                                    {{ keyFeature.replace("*", "").trim() }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <!---->
                </div>
                <div class="details">
                    <div class="section">
                        <div class="label">{{ $t("chatbotv2.lastUpdate") }}</div>
                        <div class="data">{{ timeHumanize(selectedTemplate.updatedAt) }}</div>
                    </div>
                    <div class="section">
                        <div class="label">{{ $t("chatbotv2.categories") }}</div>
                        <div class="data" style="flex-direction: column; align-items: flex-start">
                            {{ selectedTemplate.categories[currentLang].join(", ") }}
                        </div>
                    </div>
                    <div class="section">
                        <div v-if="$i18n.locale === 'en' || $i18n.locale === 'ru'" class="label">
                            {{ $t("chatbotv2.worksWith") }}
                        </div>
                        <div class="data" style="flex-direction: column; align-items: flex-start">
                            <div class="display-integrations">
                                <img class="icon supsis" src="/static/img/supsis-logo3.png" />

                                <WhatsappV1 :color="'#28AC01'" class="templates-whatsapp-icon" />
                                <WhatsappV2 :color="'#28AC01'" class="templates-whatsapp-icon" />
                                <WhatsappBusinessIcon :color="'#28AC01'" class="templates-whatsapp-icon" />
                                <TelegramIcon />
                                <InstagramIcon special-background-mode />
                                <MessengerIcon />
                            </div>
                        </div>
                        <div v-if="$i18n.locale === 'tr'" class="label">{{ $t("chatbotv2.worksWith") }}</div>
                    </div>
                </div>
            </div>
        </a-modal>

        <!-- AI Bot Wizard Modal -->
        <a-modal
            centered
            :visible="aiBotWizard"
            :footer="null"
            :closable="true"
            :width="820"
            :maskClosable="false"
            @cancel="closeAIBotWizard"
            class="ai-bot-wizard-modal"
        >
            <div class="ai-bot-wizard">
                <!-- Steps Indicator -->
                <div class="wizard-steps-indicator">
                    <div class="wizard-step-item" :class="{ active: wizardStep === 2, done: wizardStep > 2 }">
                        <div class="step-circle">
                            <i class="material-icons" v-if="wizardStep > 2">check</i>
                            <span v-else>1</span>
                        </div>
                        <span class="step-label">{{ $t("chatbotv2.ragMethod") }}</span>
                    </div>
                    <div class="step-connector" :class="{ done: wizardStep > 2 }"></div>
                    <template v-if="wizardRagMethod === 'supsis-rag'">
                        <div class="wizard-step-item" :class="{ active: wizardStep === 3, done: wizardStep > 3 }">
                            <div class="step-circle">
                                <i class="material-icons" v-if="wizardStep > 3">check</i>
                                <span v-else>2</span>
                            </div>
                            <span class="step-label">{{ $t("chatbotv2.embedding") }}</span>
                        </div>
                        <div class="step-connector" :class="{ done: wizardStep > 3 }"></div>
                        <div class="wizard-step-item" :class="{ active: wizardStep === 4 }">
                            <div class="step-circle"><span>3</span></div>
                            <span class="step-label">{{ $t("chatbotv2.llmModel") }}</span>
                        </div>
                    </template>
                </div>

                <!-- Step 2: RAG Yöntemi -->
                <div v-if="wizardStep === 2" class="wizard-step-content">
                    <div class="wizard-step-title">{{ $t("chatbotv2.wizardStepTitleRag") }}</div>
                    <div class="wizard-step-desc">{{ $t("chatbotv2.wizardStepDescRag") }}</div>

                    <div class="wizard-rag-cards">
                        <div
                            class="wizard-rag-card"
                            :class="{ selected: wizardRagMethod === 'supsis-rag' }"
                            @click="wizardRagMethod = 'supsis-rag'"
                        >
                            <div class="wizard-rag-card-header">
                                <span class="card-title">{{ $t("chatbotv2.supsisRag") }}</span>
                                <a-tag color="blue">{{ $t("chatbotv2.new") }}</a-tag>
                                <a-tag v-if="wizardRagMethod === 'supsis-rag'" color="green">{{ $t("chatbotv2.selected") }}</a-tag>
                            </div>
                            <div class="card-desc">
                                {{ $t("chatbotv2.supsisRagDesc") }}
                            </div>
                        </div>
                        <div
                            class="wizard-rag-card"
                            :class="{ selected: wizardRagMethod === 'openai-rag' }"
                            @click="wizardRagMethod = 'openai-rag'"
                        >
                            <div class="wizard-rag-card-header">
                                <img src="https://openai.com/favicon.ico" class="rag-logo" onerror="this.style.display='none'" />
                                <span class="card-title">{{ $t("chatbotv2.openaiRag") }}</span>
                                <a-tag v-if="wizardRagMethod === 'openai-rag'" color="green">{{ $t("chatbotv2.selected") }}</a-tag>
                            </div>
                            <div class="card-desc">
                                {{ $t("chatbotv2.openaiRagDesc") }}
                            </div>
                        </div>
                    </div>

                    <!-- Supsis RAG: kanal seçimi -->
                    <div v-if="wizardRagMethod === 'supsis-rag'" class="wizard-channel-section">
                        <div class="section-label">{{ $t("chatbotv2.openRouterChannel") }} <span class="required-star">*</span></div>
                        <div class="section-desc">{{ $t("chatbotv2.openRouterChannelDesc") }}</div>
                        <a-select
                            v-model="wizardOpenrouterChannel"
                            :placeholder="$t('chatbotv2.selectOpenRouterChannel')"
                            style="width: 100%"
                        >
                            <a-select-option v-for="ch in openrouterChannels" :key="ch._id" :value="ch._id">
                                {{ ch.name }}
                                <span v-if="ch.payload && ch.payload.keyMode === 'byok'" style="color: #1890ff"> (BYOK - %10 ücret)</span>
                                <span v-else style="color: #52c41a"> (Supsis API)</span>
                            </a-select-option>
                        </a-select>
                        <div v-if="openrouterChannels.length === 0" style="margin-top: 10px">
                            <a-alert
                                type="warning"
                                show-icon
                                :message="$t('chatbotv2.noOpenRouterChannel')"
                                :description="$t('chatbotv2.createOpenRouterChannelDesc')"
                            />
                        </div>
                    </div>

                    <!-- OpenAI RAG: kanal kontrolü -->
                    <div v-if="wizardRagMethod === 'openai-rag'" class="wizard-channel-section">
                        <div v-if="openAIChannel">
                            <a-alert type="success" show-icon :message="$t('chatbotv2.openaiChannelExists')" />
                        </div>
                        <div v-else>
                            <a-alert
                                type="error"
                                show-icon
                                :message="$t('chatbotv2.openaiChannelNotFound')"
                                :description="$t('chatbotv2.createOpenaiChannelDesc')"
                            />
                        </div>
                    </div>
                </div>

                <!-- Step 3: Embedding Modeli (Supsis RAG) -->
                <div v-if="wizardStep === 3" class="wizard-step-content">
                    <div class="wizard-step-title">{{ $t("chatbotv2.wizardStepTitleEmbedding") }}</div>
                    <div class="wizard-step-desc">{{ $t("chatbotv2.wizardStepDescEmbedding") }}</div>

                    <div class="wizard-embedding-cards">
                        <div
                            v-for="em in wizardEmbeddingModels"
                            :key="em.id"
                            class="wizard-embedding-card"
                            :class="{ selected: wizardEmbeddingModel === em.id }"
                            @click="wizardEmbeddingModel = em.id"
                        >
                            <div class="em-header">
                                <span class="em-name">{{ em.name }}</span>
                                <a-tag v-if="em.recommended" color="green">{{ $t("chatbotv2.recommended") }}</a-tag>
                                <i class="material-icons em-check" v-if="wizardEmbeddingModel === em.id">check_circle</i>
                            </div>
                            <div class="em-meta">
                                <span>{{ em.contextLength }} token</span>
                                <span>{{ em.dimensions }} boyut</span>
                            </div>
                            <div class="em-desc">{{ em.description }}</div>
                        </div>
                    </div>
                </div>

                <!-- Step 4: LLM Modeli + Routing + Fallback (Supsis RAG) -->
                <div v-if="wizardStep === 4" class="wizard-step-content wizard-llm-step">
                    <div class="wizard-step-title">{{ $t("chatbotv2.wizardStepTitleLlm") }}</div>
                    <div class="wizard-step-desc">{{ $t("chatbotv2.wizardStepDescLlm") }}</div>

                    <div class="wizard-selected-model-bar">
                        <span class="selected-label">{{ $t("chatbotv2.selected") }}:</span>
                        <b class="selected-model-name">{{ wizardLLMModel }}</b>
                        <a-button
                            v-if="!wizardORLoading && wizardORModels.length === 0 && !wizardORError"
                            type="link"
                            size="small"
                            @click="loadWizardORModels"
                        >
                            <i class="material-icons" style="font-size: 14px; vertical-align: middle">refresh</i>
                            {{ $t("chatbotv2.loadModels") }}
                        </a-button>
                        <a-button
                            v-if="wizardORError"
                            type="link"
                            size="small"
                            @click="loadWizardORModels"
                        >
                            {{ $t("chatbotv2.retry") }}
                        </a-button>
                    </div>

                    <div v-if="wizardORLoading" class="wizard-or-loading">
                        <a-spin :tip="$t('chatbotv2.loadingModels')" />
                    </div>

                    <div v-if="wizardORError && !wizardORLoading" style="margin-bottom: 10px">
                        <a-alert type="error" :message="wizardORError" show-icon />
                    </div>

                    <template v-if="!wizardORLoading && wizardORModels.length > 0">
                        <a-input
                            v-model="wizardORSearchQuery"
                            :placeholder="$t('chatbotv2.search')"
                            allow-clear
                            style="margin-bottom: 12px"
                            @change="wizardORPage = 1"
                        >
                            <i class="material-icons" slot="prefix" style="font-size: 16px; color: #bbb; line-height: 1">search</i>
                        </a-input>

                        <div class="wizard-or-cards">
                            <div
                                v-for="m in wizardPaginatedORModels"
                                :key="m.id"
                                class="wizard-or-card"
                                :class="{ 'is-selected': wizardLLMModel === m.id }"
                                @click="wizardLLMModel = m.id"
                            >
                                <div class="selected-ribbon" v-if="wizardLLMModel === m.id">
                                    <i class="material-icons">check_circle</i> {{ $t("chatbotv2.selected") }}
                                </div>
                                <div class="or-provider">{{ getWizardProvider(m.id) }}</div>
                                <div class="or-model-name">{{ m.name || m.id }}</div>
                                <div class="or-model-id">{{ m.id }}</div>
                                <div class="or-stats">
                                    <span class="stat" v-if="m.context_length">{{ formatWizardContextLength(m.context_length) }}</span>
                                    <span class="stat">{{ formatWizardPrice(m, 'prompt') }}/1M in</span>
                                </div>
                            </div>
                        </div>

                        <div class="wizard-or-pagination" v-if="wizardFilteredORModels.length > wizardORPageSize">
                            <a-pagination
                                :current="wizardORPage"
                                :total="wizardFilteredORModels.length"
                                :page-size="wizardORPageSize"
                                size="small"
                                @change="onWizardPageChange"
                            />
                        </div>
                    </template>

                    <div class="wizard-routing-section">
                        <div class="section-label">{{ $t("chatbotv2.routingStrategy") }}</div>
                        <a-radio-group v-model="wizardRoutingStrategy" size="small">
                            <a-radio-button value="default">Fiyat Dengeli</a-radio-button>
                            <a-radio-button value="price">En Ucuz</a-radio-button>
                            <a-radio-button value="throughput">En Hızlı</a-radio-button>
                            <a-radio-button value="latency">En Düşük Gecikme</a-radio-button>
                        </a-radio-group>
                    </div>

                    <div class="wizard-fallback-section" v-if="wizardORModels.length > 0">
                        <div class="section-label">{{ $t("chatbotv2.fallbackModels") }} <span class="section-hint">({{ $t("chatbotv2.optional") }})</span></div>
                        <div class="section-desc">{{ $t("chatbotv2.fallbackModelsDesc") }}</div>
                        <a-select
                            mode="multiple"
                            v-model="wizardFallbacks"
                            :placeholder="$t('chatbotv2.selectFallbackModel')"
                            style="width: 100%"
                            :filter-option="wizardFallbackFilter"
                        >
                            <a-select-option
                                v-for="m in wizardFilteredORModels.slice(0, 100)"
                                :key="m.id"
                                :value="m.id"
                            >
                                {{ m.id }}
                            </a-select-option>
                        </a-select>
                    </div>
                </div>

                <!-- Wizard Footer -->
                <div class="wizard-footer">
                    <a-button @click="closeAIBotWizard">{{ $t("message.cancel") }}</a-button>
                    <div class="wizard-footer-right">
                        <a-button v-if="wizardStep > 2" @click="wizardBack">
                            <i class="material-icons" style="font-size: 16px; vertical-align: middle">arrow_back</i>
                            {{ $t("chatbotv2.backup") }}
                        </a-button>
                        <a-button
                            type="primary"
                            @click="wizardNext"
                            :disabled="!wizardCanNext"
                            :loading="wizardCreating"
                        >
                            {{ wizardNextLabel === 'Oluştur' ? $t("message.create") : $t("chatbotv2.continue") }}
                            <i
                                class="material-icons"
                                style="font-size: 16px; vertical-align: middle; margin-left: 4px"
                                v-if="!wizardCreating && wizardNextLabel !== 'Oluştur'"
                            >arrow_forward</i>
                        </a-button>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>
<script>
import Vue from "vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import WebsiteChatbotBuilder from "./WebsiteChatbotBuilder.vue";
import { mapActions, mapState } from "vuex";
import Modal2 from "../../../../../components/common/Modal2.vue";
import ApiKeyIcon from "../integrations/svg/ApiKeyIcon.vue";
import CHATBOT_TEMPLATES from "../templates/chatbot-templates";
import _ from "lodash";
import CONFIG from "../../../../../config/config";
import TelegramIcon from "../integrations/svg/TelegramIcon.vue";
import moment from "moment/moment";
import CHATBOT_AI_TEMPLATES from "../templates/chatbot-ai-templates";
import WhatsappV2 from "../integrations/svg/specialWhatsappIcon/WhatsappV2.vue";
import WhatsappV1 from "../integrations/svg/specialWhatsappIcon/WhatsappV1.vue";
import WhatsappBusinessIcon from "../integrations/svg/WhatsappBusinessIcon.vue";
import InstagramIcon from "../integrations/svg/InstagramIcon.vue";
import MessengerIcon from "../integrations/svg/MessengerIcon.vue";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";

const WIZARD_EMBEDDING_MODELS = [
    {
        id: "voyage-4-lite",
        name: "voyage-4-lite",
        recommended: false,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Düşük maliyet, yüksek hız. Yüksek hacimli uygulamalar için optimize edilmiştir.",
    },
    {
        id: "voyage-4",
        name: "voyage-4",
        recommended: true,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Dengelenmiş performans. Genel metin araması için önerilen model.",
    },
    {
        id: "voyage-4-large",
        name: "voyage-4-large",
        recommended: false,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Maksimum doğruluk. Karmaşık semantik ilişkiler için idealdir.",
    },
    {
        id: "voyage-code-3",
        name: "voyage-code-3",
        recommended: false,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Kod araması ve teknik dokümantasyon için optimize edilmiştir.",
    },
];

const DEFAULT_LLM_MODEL = "google/gemini-3-flash-preview";

export default Vue.extend({
    name: "CreateChatbot",
    components: {
        TelegramIcon,
        WebsiteChatbotBuilder,
        SupsisButton,
        ApiKeyIcon,
        Modal2,
        WhatsappV1,
        WhatsappV2,
        WhatsappBusinessIcon,
        InstagramIcon,
        MessengerIcon,
    },
    data() {
        return {
            websiteBuilder: false,
            openAIModal: false,
            templateItems: CHATBOT_TEMPLATES,
            aiTemplateItems: CHATBOT_AI_TEMPLATES,
            akinonEnabled: CONFIG.AKINON.enabledReal,
            enabledSlugs: [],
            selectedTemplate: null,
            importing: false,
            cancels: [],
            currentLang: "",

            // AI Bot Wizard
            aiBotWizard: false,
            wizardStep: 2,
            wizardRagMethod: "supsis-rag",
            wizardOpenrouterChannel: null,
            wizardEmbeddingModel: "voyage-4",
            wizardEmbeddingModels: WIZARD_EMBEDDING_MODELS,
            wizardLLMModel: DEFAULT_LLM_MODEL,
            wizardRoutingStrategy: "default",
            wizardFallbacks: [],
            wizardCreating: false,
            wizardORModels: [],
            wizardORLoading: false,
            wizardORError: null,
            wizardORSearchQuery: "",
            wizardORPage: 1,
            wizardORPageSize: 9,
            wizardORSubscription: null,
        };
    },
    computed: {
        ...mapState({
            openAIChannel: (state) => state.channel.channels.find((x) => x.type === "openai"),
            openrouterChannels: (state) =>
                (state.channel.channels || []).filter(function (c) {
                    return c.type === "openrouter" && !c.isDeleted;
                }),
        }),
        wizardCanNext() {
            if (this.wizardStep === 2) {
                if (this.wizardRagMethod === "supsis-rag") {
                    return !!this.wizardOpenrouterChannel;
                }
                if (this.wizardRagMethod === "openai-rag") {
                    return !!this.openAIChannel;
                }
                return false;
            }
            if (this.wizardStep === 3) return true;
            if (this.wizardStep === 4) return !!this.wizardLLMModel;
            return true;
        },
        wizardNextLabel() {
            if (this.wizardStep === 2 && this.wizardRagMethod === "openai-rag") return "Oluştur";
            if (this.wizardStep === 4) return "Oluştur";
            return "Devam";
        },
        wizardFilteredORModels() {
            var q = (this.wizardORSearchQuery || "").toLowerCase();
            return this.wizardORModels.filter(function (m) {
                if (!q) return true;
                return (
                    m.id.toLowerCase().indexOf(q) !== -1 ||
                    (m.name || "").toLowerCase().indexOf(q) !== -1
                );
            });
        },
        wizardPaginatedORModels() {
            var start = (this.wizardORPage - 1) * this.wizardORPageSize;
            return this.wizardFilteredORModels.slice(start, start + this.wizardORPageSize);
        },
    },
    methods: {
        ...mapActions({
            createEmptyStory: "CB/createEmptyStory",
            createStoryFromTemplate: "CB/createStoryFromTemplate",
            listStories: "CB/listStories",
        }),
        isThatAI(selectedTemplate) {
            return selectedTemplate.categories.en.includes("artificial-intelligent");
        },

        createStory(slug) {
            this.importing = true;
            this.createStoryFromTemplate(slug)
                .then((result) => {
                    if (!!result) {
                        this.selectedTemplate = null;
                        this.$message.success({
                            content: `Story imported successfully`,
                            duration: 2,
                        });
                        setTimeout(() => {
                            this.$router.push(`/chatbot/story/${result.story._id}/${result.board.version}`);
                        }, 500);
                    } else {
                        this.$message.error({
                            content: `Story import failed`,
                            duration: 2,
                        });
                    }
                })
                .catch((err) => {
                    this.$message.error({
                        content: `Story import failed`,
                        duration: 2,
                    });
                })
                .finally(() => {
                    this.importing = false;
                });
        },
        timeHumanize(date) {
            return moment(date).calendar();
        },
        close() {
            this.$emit("close");
        },
        createBlankStory() {
            const hide = this.$message.loading({
                content: this.$t('chatbotv2.creatingBlank'),
                duration: 20,
            });
            this.createEmptyStory({
                title: this.$t('chatbotv2.untitledChatbot'),
                description: "",
            })
                .then(({ story }) => {
                    this.$router.push(`/chatbot/story/${story._id}/1.0`);
                })
                .catch((err) => {
                    this.$message.error({
                        content: this.$t('chatbotv2.errorCreating'),
                        duration: 3,
                    });
                })
                .finally(() => {
                    hide();
                    this.listStories();
                });
        },
        channelNotAvailable() {
            if (this.openAIChannel) {
                this.websiteBuilder = true;
            } else {
                this.openAIModal = true;
            }
        },
        openAIChannelCreate() {
            this.$router.push(`/integrations/openai`);
        },
        getFilteredTemplates() {
            const q = (this.search || "").trim().toLowerCase();
            return this.templateItems
                .filter((x) => x.visible)
                .filter((item) => {
                    if (_.isEmpty(this.enabledSlugs)) {
                        return _.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q);
                    } else {
                        return (
                            this.enabledSlugs.includes(item.slug) &&
                            (_.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q))
                        );
                    }
                });
        },
        getFilteredAiTemplates() {
            const q = (this.search || "").trim().toLowerCase();
            return this.aiTemplateItems
                .filter((x) => x.visible)
                .filter((item) => {
                    if (_.isEmpty(this.enabledSlugs)) {
                        return _.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q);
                    } else {
                        return (
                            this.enabledSlugs.includes(item.slug) &&
                            (_.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q))
                        );
                    }
                });
        },

        // ===== AI Bot Wizard =====
        openAIBotWizard() {
            this.wizardStep = 2;
            this.wizardRagMethod = "supsis-rag";
            this.wizardOpenrouterChannel = null;
            this.wizardEmbeddingModel = "voyage-4";
            this.wizardLLMModel = DEFAULT_LLM_MODEL;
            this.wizardRoutingStrategy = "default";
            this.wizardFallbacks = [];
            this.wizardORModels = [];
            this.wizardORError = null;
            this.wizardORSearchQuery = "";
            this.wizardORPage = 1;
            this.aiBotWizard = true;
        },
        closeAIBotWizard() {
            this.aiBotWizard = false;
            if (this.wizardORSubscription) {
                this.wizardORSubscription.unsubscribe();
                this.wizardORSubscription = null;
            }
        },
        wizardBack() {
            if (this.wizardStep > 2) {
                this.wizardStep -= 1;
            }
        },
        wizardNext() {
            if (this.wizardStep === 2) {
                if (this.wizardRagMethod === "openai-rag") {
                    this.wizardCreate();
                    return;
                }
                this.wizardStep = 3;
            } else if (this.wizardStep === 3) {
                this.wizardStep = 4;
                this.loadWizardORModels();
            } else if (this.wizardStep === 4) {
                this.wizardCreate();
            }
        },
        async wizardCreate() {
            this.wizardCreating = true;
            try {
                const { story } = await this.createEmptyStory({
                    title: this.$t("chatbotv2.untitledChatbot"),
                    description: "",
                });
                const storyId = story._id;

                await CHATBOT_API.updateStorySingleKey({ id: storyId, key: "ai.ragMethod", value: this.wizardRagMethod });

                if (this.wizardRagMethod === "supsis-rag") {
                    await CHATBOT_API.updateStorySingleKey({ id: storyId, key: "ai.openrouterChannel", value: this.wizardOpenrouterChannel });
                    await CHATBOT_API.updateStorySingleKey({ id: storyId, key: "ai.embeddingModel", value: this.wizardEmbeddingModel });
                    await CHATBOT_API.updateStorySingleKey({ id: storyId, key: "ai.llmModel", value: this.wizardLLMModel });
                    await CHATBOT_API.updateStorySingleKey({ id: storyId, key: "ai.routingStrategy", value: this.wizardRoutingStrategy });
                    if (this.wizardFallbacks.length > 0) {
                        await CHATBOT_API.updateStorySingleKey({ id: storyId, key: "ai.fallbackModels", value: this.wizardFallbacks });
                    }
                }

                this.listStories();
                this.$router.push("/chatbot/story/" + storyId + "/1.0");
            } catch (e) {
                this.$message.error({
                    content: this.$t("chatbotv2.errorCreating"),
                    duration: 3,
                });
            } finally {
                this.wizardCreating = false;
            }
        },
        loadWizardORModels() {
            var self = this;
            if (!this.wizardOpenrouterChannel) {
                self.wizardORError = "OpenRouter kanalı seçili değil.";
                return;
            }
            self.wizardORLoading = true;
            self.wizardORError = null;

            var siteId = this.$store.state.site.site._id;
            var userId = this.$store.state.auth.user._id;

            sendSocketMessage("user openrouter-list-models", {
                siteId: siteId,
                userId: userId,
                channelId: this.wizardOpenrouterChannel,
            });

            if (self.wizardORSubscription) {
                self.wizardORSubscription.unsubscribe();
            }
            self.wizardORSubscription = socketMessageStream.subscribe(function (msg) {
                if (msg.cmd === "openrouter_list_models_result") {
                    self.wizardORLoading = false;
                    if (msg.data && msg.data.success && Array.isArray(msg.data.models)) {
                        self.wizardORModels = msg.data.models;
                    } else {
                        self.wizardORError = (msg.data && msg.data.message) || "Model listesi alınamadı.";
                    }
                    if (self.wizardORSubscription) {
                        self.wizardORSubscription.unsubscribe();
                        self.wizardORSubscription = null;
                    }
                }
            });

            setTimeout(function () {
                if (self.wizardORLoading) {
                    self.wizardORLoading = false;
                    self.wizardORError = "Model listesi yüklenemedi. Lütfen tekrar deneyin.";
                }
            }, 15000);
        },
        onWizardPageChange(page) {
            this.wizardORPage = page;
        },
        getWizardProvider(modelId) {
            if (!modelId) return "";
            var parts = modelId.split("/");
            return parts.length > 1 ? parts[0] : "";
        },
        formatWizardContextLength(len) {
            if (!len) return "—";
            if (len >= 1000000) return (len / 1000000).toFixed(0) + "M";
            if (len >= 1000) return (len / 1000).toFixed(0) + "K";
            return String(len);
        },
        formatWizardPrice(m, type) {
            try {
                var price = m.pricing && m.pricing[type] ? parseFloat(m.pricing[type]) : null;
                if (price === null || isNaN(price)) return "—";
                if (price === 0) return "Free";
                return "$" + (price * 1000000).toFixed(4);
            } catch (e) {
                return "—";
            }
        },
        wizardFallbackFilter(input, option) {
            return option.key.toLowerCase().indexOf(input.toLowerCase()) !== -1;
        },
    },
    mounted() {
        this.enabledSlugs = this.akinonEnabled ? ["us-polo-assn", "pierre-cardin", "cacharel"] : [];
        const escapeToEscape = (e) => {
            let val = e.key;
            if (e.key === "Escape") {
                this.close();
            }
        };
        window.addEventListener("keydown", escapeToEscape);

        this.currentLang = this.$i18n.locale === "en" || this.$i18n.locale === "tr" ? this.$i18n.locale : "en";
        this.cancels.push(() => {
            window.removeEventListener("keydown", escapeToEscape);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
        if (this.wizardORSubscription) {
            this.wizardORSubscription.unsubscribe();
        }
    },
});
</script>

<style scoped lang="scss">
.create-chatbot-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100%;
    height: 100%;
    background: white;
    z-index: 999 !important;
}

.create-chatbot {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 80px;
    position: relative;
    height: 100%;
    overflow-y: auto;
    width: 100%;

    .sticky-header {
        background: #fff;
        max-width: 1600px;
        padding: 40px 0;
        position: sticky;
        top: 0;
        width: 100%;
        z-index: 999 !important;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .title {
            font-size: 30px;
            line-height: 38px;
            color: #22272d;
            font-weight: 700;
        }

        .description {
            font-size: 18px;
            line-height: 23px;
        }

        .btnCloseCb {
            position: absolute;
            right: 0;
        }
    }

    .items {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .divider-items {
            width: 100%;
            display: flex;
            gap: 7px;

            .half-template-area {
                width: 50%;

                .half-template-items {
                    display: flex;
                    width: 100%;
                    flex-wrap: wrap;
                    gap: 5px;

                    .block-template {
                        max-width: 300px !important;
                        height: 200px !important;
                    }
                }
            }
        }

        .block {
            background-color: #fff;
            border: 2px solid #f5f5f5;
            border-radius: 15px;
            cursor: pointer;
            padding: 20px;
            transition: border-color 0.1s;
            width: 400px;
            height: 175px;

            &:hover {
                border-color: #006cff !important;
            }

            .name {
                color: #22272d;
                font-size: 18px;
                font-weight: 600;
                line-height: 23px;
                margin-bottom: 8px;
                margin-top: 13px;
            }

            .description {
                color: #22272d;
                font-size: 16px;
                line-height: 20px;
            }

            &.block-scratch {
                .icon {
                    background: #f5f5f5;
                    border-radius: 100%;
                    display: flex;
                    height: 34px;
                    width: 34px;
                    align-items: center;
                    justify-content: center;
                }
            }

            &.block-ai {
                background: #fff;
                border-color: #f5f5f5;
                position: relative;

                .ai-badge {
                    display: flex;
                    font-size: 12px;
                    font-weight: 700;
                    line-height: 16px;
                    justify-content: center;
                    align-items: center;
                    position: absolute;
                    right: 10px;
                    top: 10px;

                    span {
                        background: #22272d;
                        border-radius: 6px;
                        color: #fff;
                        margin-left: 4px;
                        padding: 0 4px;

                        &.lang {
                            background: #f5f5f5;
                            color: #22272d;
                        }
                    }
                }

                .icon {
                    color: #006cff;
                    height: 34px;
                    margin-bottom: 12px;
                    width: 34px;
                }
            }

            &.block-ai-bot {
                background: linear-gradient(135deg, #f0f5ff 0%, #f9f0ff 100%);
                border-color: #d3adf7;

                &:hover {
                    border-color: #722ed1 !important;
                }

                .ai-badge span {
                    background: linear-gradient(90deg, #722ed1, #1890ff);
                }

                .ai-badge .default-tag {
                    background: #f6ffed;
                    color: #389e0d;
                    border: 1px solid #b7eb8f;
                    border-radius: 6px;
                    padding: 0 4px;
                    font-size: 11px;
                }

                .icon {
                    color: #722ed1;
                }
            }
        }
    }
}

.open-ai-modal-body {
    display: block;
    justify-content: center;
    align-items: center;
    height: 100%;

    .open-ai-modal-text {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        color: #181c32;
        margin-bottom: 20px;
        text-align: center;
    }

    .delete-modal-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 50px;

        .push-btn {
            background: #ff7900;
            border: none;
            outline: none;
            color: #ffffff;
            font-size: 14px;
            font-weight: 500;
            padding: 10px 20px;
            border-radius: 4px;
        }

        .push-btn-give-up {
            background: transparent;
            border: 1px solid #ff7900;
            outline: none;
            color: #ff7900;
            font-size: 14px;
            font-weight: 500;
            padding: 10px 20px;
            border-radius: 4px;
        }
    }
}

// ===== AI Bot Wizard =====
.ai-bot-wizard {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .wizard-steps-indicator {
        display: flex;
        align-items: center;
        gap: 0;
        padding: 4px 0 16px;
        border-bottom: 1px solid #f0f0f0;
        margin-bottom: 4px;

        .wizard-step-item {
            display: flex;
            align-items: center;
            gap: 8px;

            .step-circle {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                background: #e5e5e5;
                color: #888;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 13px;
                font-weight: 700;
                flex-shrink: 0;

                .material-icons {
                    font-size: 15px;
                }
            }

            .step-label {
                font-size: 13px;
                color: #888;
                white-space: nowrap;
            }

            &.active {
                .step-circle {
                    background: #1890ff;
                    color: #fff;
                }
                .step-label {
                    color: #1890ff;
                    font-weight: 600;
                }
            }

            &.done {
                .step-circle {
                    background: #52c41a;
                    color: #fff;
                }
                .step-label {
                    color: #52c41a;
                }
            }
        }

        .step-connector {
            flex: 1;
            height: 2px;
            background: #e5e5e5;
            margin: 0 10px;
            min-width: 30px;

            &.done {
                background: #52c41a;
            }
        }
    }

    .wizard-step-content {
        min-height: 300px;

        .wizard-step-title {
            font-size: 18px;
            font-weight: 700;
            color: #22272d;
            margin-bottom: 6px;
        }

        .wizard-step-desc {
            font-size: 14px;
            color: #666;
            margin-bottom: 20px;
        }
    }

    .wizard-rag-cards {
        display: flex;
        gap: 16px;
        margin-bottom: 20px;

        .wizard-rag-card {
            flex: 1;
            border: 2px solid #e5e5e5;
            border-radius: 12px;
            padding: 18px;
            cursor: pointer;
            transition: all 0.2s;
            background: #fff;

            &:hover {
                border-color: #1890ff;
            }

            &.selected {
                border-color: #1890ff;
                background: #e6f7ff;
            }

            .wizard-rag-card-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 10px;

                .rag-logo {
                    width: 18px;
                    height: 18px;
                }

                .card-title {
                    font-size: 15px;
                    font-weight: 600;
                    flex: 1;
                }
            }

            .card-desc {
                font-size: 13px;
                color: #555;
                line-height: 1.5;
            }
        }
    }

    .wizard-channel-section {
        background: #fafafa;
        border: 1px solid #e5e5e5;
        border-radius: 10px;
        padding: 16px;

        .section-label {
            font-size: 14px;
            font-weight: 600;
            color: #22272d;
            margin-bottom: 4px;

            .required-star {
                color: red;
            }
        }

        .section-desc {
            font-size: 12px;
            color: #888;
            margin-bottom: 10px;
        }
    }

    .wizard-embedding-cards {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;

        .wizard-embedding-card {
            border: 2px solid #e5e5e5;
            border-radius: 10px;
            padding: 14px;
            cursor: pointer;
            transition: all 0.2s;
            background: #fff;

            &:hover {
                border-color: #1890ff;
            }

            &.selected {
                border-color: #1890ff;
                background: #e6f7ff;
            }

            .em-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 6px;

                .em-name {
                    font-weight: 600;
                    font-size: 13px;
                    font-family: monospace;
                    flex: 1;
                }

                .em-check {
                    font-size: 18px;
                    color: #1890ff;
                }
            }

            .em-meta {
                display: flex;
                gap: 10px;
                font-size: 11px;
                color: #888;
                margin-bottom: 6px;
            }

            .em-desc {
                font-size: 12px;
                color: #555;
                line-height: 1.4;
            }
        }
    }

    .wizard-llm-step {
        .wizard-selected-model-bar {
            display: flex;
            align-items: center;
            gap: 8px;
            background: #e6f7ff;
            border: 1px solid #91d5ff;
            border-radius: 8px;
            padding: 8px 12px;
            margin-bottom: 14px;
            font-size: 13px;

            .selected-label {
                color: #555;
            }

            .selected-model-name {
                font-family: monospace;
                font-size: 12px;
                color: #096dd9;
                flex: 1;
            }
        }

        .wizard-or-loading {
            display: flex;
            justify-content: center;
            padding: 40px 0;
        }

        .wizard-or-cards {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin-bottom: 12px;

            .wizard-or-card {
                border: 2px solid #e5e5e5;
                border-radius: 10px;
                padding: 12px;
                cursor: pointer;
                transition: all 0.15s;
                background: #fff;
                position: relative;
                overflow: hidden;

                &:hover {
                    border-color: #1890ff;
                }

                &.is-selected {
                    border-color: #1890ff;
                    background: linear-gradient(180deg, #fff 0%, #f0f8ff 100%);
                }

                .selected-ribbon {
                    position: absolute;
                    top: -1px;
                    right: -1px;
                    background: #1890ff;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    gap: 3px;
                    padding: 3px 8px;
                    border-radius: 0 8px 0 8px;
                    font-size: 11px;
                    font-weight: 600;

                    .material-icons {
                        font-size: 12px;
                    }
                }

                .or-provider {
                    font-size: 10px;
                    color: #aaa;
                    font-weight: 500;
                    margin-bottom: 3px;
                    text-transform: capitalize;
                }

                .or-model-name {
                    font-size: 13px;
                    font-weight: 600;
                    color: #222;
                    margin-bottom: 2px;
                    line-height: 1.3;
                }

                .or-model-id {
                    font-family: monospace;
                    font-size: 10px;
                    color: #999;
                    margin-bottom: 8px;
                    word-break: break-all;
                }

                .or-stats {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;

                    .stat {
                        font-size: 11px;
                        color: #666;
                        background: #f5f5f5;
                        padding: 1px 5px;
                        border-radius: 4px;
                    }
                }
            }
        }

        .wizard-or-pagination {
            display: flex;
            justify-content: center;
            margin-bottom: 16px;
        }
    }

    .wizard-routing-section {
        background: #fafafa;
        border: 1px solid #e5e5e5;
        border-radius: 10px;
        padding: 14px 16px;
        margin-top: 12px;

        .section-label {
            font-size: 13px;
            font-weight: 600;
            color: #22272d;
            margin-bottom: 10px;
        }
    }

    .wizard-fallback-section {
        background: #fafafa;
        border: 1px solid #e5e5e5;
        border-radius: 10px;
        padding: 14px 16px;
        margin-top: 10px;

        .section-label {
            font-size: 13px;
            font-weight: 600;
            color: #22272d;
            margin-bottom: 4px;

            .section-hint {
                font-size: 11px;
                color: #888;
                font-weight: 400;
                margin-left: 4px;
            }
        }

        .section-desc {
            font-size: 12px;
            color: #888;
            margin-bottom: 10px;
        }
    }

    .wizard-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid #f0f0f0;
        padding-top: 16px;
        margin-top: 4px;

        .wizard-footer-right {
            display: flex;
            gap: 10px;
        }
    }
}
</style>
<style lang="scss">
.open-ai-wrapper {
    .ant-modal-wrap {
        z-index: 10001 !important;
    }

    .ant-modal-mask {
        z-index: 10001 !important;
    }
}

.create-chatbot-wrapper {
    .template-modal {
        .ant-modal {
            z-index: 10001 !important;
        }
    }
}

.items {
    .template-divider {
        margin-top: 40px;
        margin-bottom: 40px;
        width: 80%;
        padding: 8px;

        .ant-divider-inner-text {
            font-size: 25px !important;
            font-weight: 700;
        }

        &::before {
            border-top: 1px solid #333333;
        }

        &::after {
            border-top: 1px solid #333333;
        }
    }
}

.ai-bot-wizard-modal {
    .ant-modal-body {
        padding: 24px;
    }

    .ant-modal-wrap {
        z-index: 10001 !important;
    }

    .ant-modal-mask {
        z-index: 10001 !important;
    }
}
</style>
